import { DescriptionType, Language } from '../enums';
import { BalloonEventModel } from '../models';
import { ImageSource } from '../types';

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?(p|li|ul|ol|tr|td)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getBestDescription(event: BalloonEventModel): string {
  const tryText = (raw: string | undefined | null): string =>
    raw ? stripHtml(raw).trim() : '';

  return (
    tryText(event.teaser.getText(Language.de)) ||
    tryText(event.getDescription(DescriptionType.MainDescription)?.getText(Language.de)) ||
    tryText(event.description.getText(Language.de)) ||
    tryText(event.complexDescription.descriptions?.find((d) => d.content?.trim().length > 0)?.content)
  );
}

function removeOldStructuredData() {
  const script = document.getElementById('structured-data');
  if (script) {
    document.head.removeChild(script);
  }
}

function createStructuredDataScript(event: BalloonEventModel) {
  const script = document.createElement('script');
  script.id = 'structured-data';
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    startDate: event.latestDate.start.toISOString(),
    endDate: event.latestDate.end
      ? event.latestDate.end.toISOString()
      : undefined,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: event.locationName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: event.address.street || undefined,
        addressLocality: event.address.locality || undefined,
        postalCode: event.address.zip || undefined,
        addressRegion: event.address.region || undefined,
        addressCountry: event.address.country || undefined,
      },
    },
    image: event.rawImages.map((image: ImageSource) => image.url),
    description: getBestDescription(event),
    organizer: event.organizer?.name
      ? { '@type': 'Organization', name: event.organizer.name }
      : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });
  document.head.appendChild(script);
}

function setTitleTag(titleContent: string) {
  let titleTag = document.querySelector('title');
  if (titleTag) {
    titleTag.textContent = titleContent;
  } else {
    titleTag = document.createElement('title');
    titleTag.textContent = titleContent;
    document.head.appendChild(titleTag);
  }
}

function setMetaDescription(description: string) {
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  } else {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    document.head.appendChild(metaDescription);
  }
}

function setMetaTags(event: BalloonEventModel, fallbackImage: string) {
  const metaTags = [
    { name: 'title', content: event.title },
    { name: 'description', content: getBestDescription(event) },
    { property: 'og:title', content: event.title },
    { property: 'og:description', content: getBestDescription(event) },
    { property: 'og:type', content: 'event' },
    {
      property: 'og:image',
      content:
        event.rawImages.length > 0 ? event.rawImages[0].url : fallbackImage,
    },
    { property: 'og:url', content: window.location.href },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: event.title },
    { name: 'twitter:description', content: getBestDescription(event) },
    {
      name: 'twitter:image',
      content: event.rawImages.length > 0 ? event.rawImages[0].url : '',
    },
  ];

  metaTags.forEach((tagData) => {
    let meta = document.querySelector(
      `meta[${tagData.name ? 'name' : 'property'}="${
        tagData.name || tagData.property
      }"]`
    );
    if (meta) {
      meta.setAttribute('content', tagData.content);
    } else {
      meta = document.createElement('meta');
      if (tagData.name) {
        meta.setAttribute('name', tagData.name);
      } else if (tagData.property) {
        meta.setAttribute('property', tagData.property);
      }
      meta.setAttribute('content', tagData.content);
      document.head.appendChild(meta);
    }
  });
}

function setCanonicalLink(event: BalloonEventModel) {
  if (event.provider && event.provider.canonicalUrl) {
    let link = document.querySelector('link[rel="canonical"]');
    if (link) {
      link.setAttribute('href', event.provider.canonicalUrl + '/' + event.slug);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', event.provider.canonicalUrl + '/' + event.slug);
      document.head.appendChild(link);
    }
  }
}
// Main function to call all methods
export function createStructuredEventData(
  event: BalloonEventModel,
  titleString: string = '###TITLE###',
  fallbackImage: string = ''
) {
  if (!event) return;

  try {
    removeOldStructuredData();
    createStructuredDataScript(event);

    const titleContent = titleString.replace('###TITLE###', event.title);
    setTitleTag(titleContent);
    setMetaDescription(getBestDescription(event));
    setMetaTags(event, fallbackImage);
    setCanonicalLink(event);
  } catch (e) {
    console.error(e);
  }
}
